
import { useEffect, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import AdminSidebar from '../components/AdminSidebar';
import CategoriesGrid from './components/CategoriesGrid';
import AddCategoryModal from './components/AddCategoryModal';
import EditCategoryModal from './components/EditCategoryModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { createCategory, deleteCategory, getCategories, updateCategory } from '../../../api/apiServices';

export default function AdminCategoriesPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );



const handleAddCategory = async (formData) => {
  try {
    const response = await createCategory(formData);

    const newCategory = response.data;

    setCategories((prev) => [...prev, newCategory]);

    setIsAddModalOpen(false);
  } catch (error) {
    console.error("Failed to add category:", error);
  }
};

const fetchCategories = async () => {
  try {
    setLoading(true);
    const response = await getCategories();
    setCategories(response.data);
  } catch (error) {
    console.error("Failed to fetch categories:", error);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchCategories();
}, []);

  const handleEditCategory = async (id, formData) => {
  try {
    const response = await updateCategory(id, formData);

    const updatedCategory = response.data;

    setCategories((prev) =>
      prev.map((c) =>
        c._id === id ? updatedCategory : c
      )
    );

    setEditingCategory(null);
  } catch (error) {
    console.error("Failed to update category:", error);
  }
};


  const handleDeleteCategory = async () => {
  try {
    if (!deletingCategory) return;

    await deleteCategory(deletingCategory._id);

    setCategories((prev) =>
      prev.filter((c) => c._id !== deletingCategory._id)
    );

    setDeletingCategory(null);
  } catch (error) {
    console.error("Failed to delete category:", error);
  }
};


  const handleReorderCategories = (reorderedCategories) => {
    setCategories(reorderedCategories);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] font-['Poppins']">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      <AdminSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="lg:ml-64 min-h-screen">
        <AdminHeader onMenuClick={() => setIsSidebarOpen(true)} />

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Categories Management
              </h1>
              <p className="text-sm text-gray-600">
                Manage your shop categories, images, and links
              </p>
            </div>

            {/* Actions Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#EF579A] focus:border-transparent"
                />
              </div>

              <button
                onClick={() => setIsAddModalOpen(true)}
                className="px-6 py-2.5 bg-[#EF579A] text-white rounded-lg text-sm font-medium hover:bg-[#d94d87] transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line text-lg"></i>
                Add Category
              </button>
            </div>

            {/* Categories Grid */}
            {loading ? (
  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
    <p className="text-gray-500">Loading categories...</p>
  </div>
) : (
  <CategoriesGrid
    categories={filteredCategories}
    onEdit={setEditingCategory}
    onDelete={setDeletingCategory}
    onReorder={handleReorderCategories}
  />
)}

            {/* Stats */}
            <div className="mt-6 text-center text-sm text-gray-500">
              Showing {filteredCategories.length} of {categories.length}{' '}
              categories
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      {isAddModalOpen && (
        <AddCategoryModal
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddCategory}
        />
      )}

      {editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={handleEditCategory}
        />
      )}

      {deletingCategory && (
        <DeleteConfirmModal
          categoryName={deletingCategory.name}
          onClose={() => setDeletingCategory(null)}
          onConfirm={handleDeleteCategory}
        />
      )}
    </div>
  );
}
